import { Component, Input, SimpleChanges, forwardRef, EventEmitter, Output, OnInit, OnChanges } from '@angular/core';
import { AbstractControl, FormGroup, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
	selector: 'app-select-autocomplete',
	templateUrl: './select-autocomplete.component.html',
	styleUrls: ['./select-autocomplete.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SelectAutocompleteComponent),
			multi: true
		}
	]
})
export class SelectAutocompleteComponent<T> implements OnInit, OnChanges {
	@Input() set group(value: AbstractControl) {
		this.formGroup = value as FormGroup;
	}
	@Input() options: T[] = [];
	@Input() label: string = '';
	@Input() placeHolder: string = '';
	@Output() selectionChange = new EventEmitter<string | null>();

	// Form configuration
	formGroup: FormGroup | undefined;
	onChange?: (value: string | null) => void;
	onTouched?: (value: boolean) => void;
	value: string | null = null;
	customControl = new FormControl<string | T>('');
	filteredOptions: T[] = [];
	private _columnLabel: string = 'name';
	private _columnValue: string = 'id';

	constructor() { }

	ngOnInit(): void {
		this.customControl.valueChanges.subscribe(() => this.filterAndUpdateOptions());
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['options'] && this.options.length > 0) {
			this.filterAndUpdateOptions();
		}
	}

	registerOnChange(onChange: typeof this.onChange): void {
		this.onChange = onChange;
	}

	registerOnTouched(onTouched: typeof this.onTouched): void {
		this.onTouched = onTouched;
	}

	writeValue(value: string | null) {
		if (value !== this.value) {
			this.selectionChange.emit(value);
		}
		this.value = value;
		this.customControl.setValue(value);

		if (this.onChange) {
			this.onChange(value);
		}

		if (this.onTouched) {
			this.onTouched(true);
		}
	}

	getOptionText(option: T | null) {
		if (!option) return '';
		const fieldValue = option[this._columnLabel as keyof T] as string;
		return fieldValue?.toString() ?? '';
	}

	getOptionValue = (option: T | null) => {
		if (!option) return '';
		const fieldValue = option[this._columnValue as keyof T] as string;
		return fieldValue?.toString() ?? '';
	};

	displayFn = (value: string): string => {
		const option = this.options.find(o => this.getOptionValue(o) === value);
		return this.getOptionText(option ?? null);
	};

	optionSelected(event: MatAutocompleteSelectedEvent) {
		const selected = event.option.value as string;
		this.writeValue(selected);
	}

	private filterAndUpdateOptions() {
		const value = this.customControl.value;
		const name = typeof value === 'string' ? value : this.getOptionText(value);
		this.filteredOptions = name ? this.filterList(name as string) : this.options.slice();
	}

	private filterList(value: string): T[] {
		const filterValue = value.toLowerCase();

		const filtered = this.options.filter(option => this.getOptionText(option).toLowerCase().includes(filterValue));
		filtered.sort((a: T, b: T) => {
			const aValue = this.getOptionText(a);
			const bValue = this.getOptionText(b);
			return aValue.toString().localeCompare(bValue.toString());
		});

		return filtered;
	}
}
